import { useState } from 'react';
import { useQueryNav } from '../../lib/nav';

/**
 * What every list in the admin area shares with Company Management: a list or
 * a grid, and ten to a page.
 */

export type View = 'list' | 'grid';

export const PAGE_SIZE = 10;

export const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** List or grid survives a refresh, so it sits in the URL; the default leaves no trace. */
export function useView(): [View, (next: View) => void] {
  const { params, set } = useQueryNav();
  const view: View = params.get('view') === 'grid' ? 'grid' : 'list';
  return [view, (next) => set({ view: next === 'list' ? null : next }, { replace: true })];
}

/**
 * One page of a list. The page asked for is held; the page shown is clamped,
 * so a filter that leaves fewer pages never strands the list on an empty one.
 */
export function usePaging<T>(items: T[]) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  return {
    shown: items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE),
    total: items.length,
    pages,
    current,
    setPage,
    /** For a filter: apply it and go back to the first page. */
    first: () => setPage(1),
  };
}

export type Paging<T> = ReturnType<typeof usePaging<T>>;

const AVATAR_COLOURS = ['#1f6fb2', '#2e9e7d', '#7a5bd0', '#d9822b', '#c0463f', '#3d7f8f', '#5d6d78'];

export const avatarColour = (id: number) => AVATAR_COLOURS[Math.abs(id) % AVATAR_COLOURS.length];

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '')).toUpperCase() || '?';
}

/** The two states of an account, as a dialog offers them. */
export const ACTIVE_STATUSES: { key: 'active' | 'inactive'; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];
